import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

import navigate from 'libs/router/navigate';

import Entity from 'components/entity/Entity';
import InfluencerSelector from 'components/influencer-selector/InfluencerSelector';
import ProfileEntityAvatar from 'components/entity/entity-avatar/ProfileEntityAvatar';
import ProfileEntityName from 'components/entity/entity-name/ProfileEntityName';

import CampaignBudgetWidget from '../campaign-budget-widget/CampaignBudgetWidget';
import CampaignImpressionsWidget from '../campaign-impressions-widget/CampaignImpressionsWidget';
import CampaignReachWidget from '../campaign-reach-widget/CampaignReachWidget';
import CreatorPerformanceByChannel from '../creator-performance-by-channel/CreatorPerformanceByChannel';
import CreatorPerformanceChannels from '../creator-performance-channels/CreatorPerformanceChannels';
import TaskProgressWidget from '../task-progress-widget/TaskProgressWidget';
import ZalandoOutfitReporting from '../zalando-outfit-reporting/ZalandoOutfitReporting';

import { fetchCreatorPerformance, resetCreatorPerformance } from '../../reducers/creator-performance';

import {
  getSupportedTaskTypes,
  isZalandoOutfitOnlyCampaign
} from 'utils/reporting';
import { __t, applyTranslations } from 'utils/language';
import { isInfluencerRealm } from 'utils/account';
import { DELIVERABLE_TYPE_ZALANDO_OUTFIT } from 'utils/deliverables';

import './creator-performance.scss';

applyTranslations(require('./creator-performance.i18n.json'));

import type { Campaign, ContentCardCreatorProfile } from 'types';
import type { CreatorPerformance as CreatorPerformanceAlias } from '../../types/CreatorPerformance.type';
import type { ReportingMetaInfoIGStorySnippetType } from 'modules/reporting/types';

type OwnProps = {
  campaignId: number,
  creatorsList: Array<ContentCardCreatorProfile>,
  igStorySnippetType: ReportingMetaInfoIGStorySnippetType
};

type StateProps = {
  campaign: Campaign,
  creatorPerformance: CreatorPerformanceAlias,
  isLoading: boolean,
  isSCP: boolean
}

type DispatchProps = {
  dispatchFetchCreatorPerformance: (campaignId: number, creatorId: number) => Promise<void>,
  dispatchResetCreatorPerformance: () => Promise<void>
}

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  selectedInfluencerId: number | null
};

class CreatorPerformance extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const selectedInfluencerId = this.props.creatorsList[0]?.id;

    this.state = {
      selectedInfluencerId
    };

    this.loadInfluencerPerformance(selectedInfluencerId);
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { selectedInfluencerId } = this.state;

    if (!this.state.selectedInfluencerId) {
      this.handleInfluencerSelected(this.props.creatorsList[0]?.id);
    }

    if (selectedInfluencerId !== prevState.selectedInfluencerId) {
      this.loadInfluencerPerformance(selectedInfluencerId);
    }
  }

  componentWillUnmount() {
    const { dispatchResetCreatorPerformance } = this.props;
    dispatchResetCreatorPerformance();
  }

  loadInfluencerPerformance = (selectedInfluencerId: number | null) => {
    const { campaignId, dispatchFetchCreatorPerformance } = this.props;

    if (selectedInfluencerId) {
      dispatchFetchCreatorPerformance(campaignId, selectedInfluencerId);
    }
  };

  handleInfluencerSelected = (selectedInfluencerId?: number | null): void => {
    const { dispatchResetCreatorPerformance } = this.props;

    if (selectedInfluencerId !== this.state.selectedInfluencerId) {
      this.setState({
        selectedInfluencerId: selectedInfluencerId ?? null
      }, dispatchResetCreatorPerformance);
    }
  };

  handleNoPostClick = (): void => {
    const { campaignId } = this.props;

    navigate.to(`/campaigns/${campaignId}/report/v2/content-metrics`);
  };

  renderInfluencerSelector() {
    const { creatorsList } = this.props;
    const { selectedInfluencerId } = this.state;

    return (
      <div className="creator-performance__influencer-selector">
        <InfluencerSelector
          influencerProfiles={creatorsList}
          selectedInfluencerProfileId={selectedInfluencerId ?? null}
          shouldSort={false}
          onSelect={this.handleInfluencerSelected}
          showSearch
        />
      </div>
    );
  }

  renderSelectedInfluencer(influencer: ContentCardCreatorProfile) {
    return (
      <div className="creator-performance__influencer">
        <Entity>
          <ProfileEntityAvatar
            profile={influencer}
            size="customSize"
            bordered
            openInNewTab
            customSize={{
              height: 80,
              width: 80
            }}
          />
          <ProfileEntityName
            profile={influencer}
            size="large"
            openInNewTab
            style={{
              fontSize: 24,
              fontWeight: 600
            }}
          />
        </Entity>
      </div>
    );
  }

  renderCreatorPerformaceInfo(influencer: ContentCardCreatorProfile) {
    const {
      campaign,
      campaignId,
      creatorPerformance,
      igStorySnippetType,
      isLoading,
      isSCP
    } = this.props;

    if (!isLoading && (!creatorPerformance.summary || isEmpty(creatorPerformance.summary))) {
      return (
        <section className="creator-performance__empty-message">
          {__t('creator_performance.no_data_error')}
        </section>
      );
    }

    const summary = creatorPerformance.summary;
    const hasSupportedTasks = getSupportedTaskTypes(isSCP)
      .some(type => creatorPerformance.agreedDeliverableTypes?.includes(type));

    if (!isLoading && !hasSupportedTasks) {
      return (
        <section className="creator-performance__empty-message">
          {__t('creator_performance.no_supported_tasks')}
        </section>
      );
    }

    const availableTaskTypes = getSupportedTaskTypes(isSCP)
      .filter(type => creatorPerformance.agreedDeliverableTypes?.includes(type));

    if (isZalandoOutfitOnlyCampaign(availableTaskTypes)) {
      return (
        <div className="creator-performance__info">
          <ZalandoOutfitReporting
            isLoading={isLoading}
            availableTaskTypes={[DELIVERABLE_TYPE_ZALANDO_OUTFIT]}
            outfitsProgress={creatorPerformance?.summary?.outfitsProgress ?? null}
            zalandoOutfit={creatorPerformance?.summary?.tasksProgress?.zalandoOutfit ?? null}
          />
        </div>
      );
    }

    return (
      <React.Fragment>
        <div className="creator-performance__info">
          <CreatorPerformanceChannels
            creatorPerformance={creatorPerformance}
            isLoading={isLoading}
          />
          <TaskProgressWidget
            tasksProgress={{
              ...(summary.tasksProgress || {})
            }}
            availableTaskTypes={availableTaskTypes}
            isLoading={isLoading}
          />
        </div>
        <CampaignBudgetWidget
          campaignId={campaignId}
          isOutdated={creatorPerformance.isBudgetSplitOutdated}
          totalBudget={summary.totalBudget}
          budget={summary.budget}
          roi={summary.roi}
          hasAgreedOffers
          isLoading={isLoading}
        />
        <CampaignReachWidget
          totalReach={summary.totalReach}
          reach={summary.reach}
          availableTaskTypes={availableTaskTypes}
          isLoading={isLoading}
        />
        <CampaignImpressionsWidget
          totalImpressions={summary.totalImpressions}
          impressions={summary.impressions}
          mediaValue={summary.mediaValue}
          availableTaskTypes={availableTaskTypes}
          isLoading={isLoading}
        />
        {
          !isEmpty(campaign) && (
            <CreatorPerformanceByChannel
              key={influencer?.id}
              campaign={campaign}
              creator={influencer}
              igStorySnippetType={igStorySnippetType}
              creatorPerformance={creatorPerformance}
              isLoading={isLoading}
            />
          )
        }
      </React.Fragment>
    );
  }

  render() {
    const { selectedInfluencerId } = this.state;
    const { creatorsList } = this.props;
    const selectedInfluencer = creatorsList.filter(i => i.id === selectedInfluencerId)[0];

    return (
      <div className="creator-performance">
        {this.renderInfluencerSelector()}
        {this.renderSelectedInfluencer(selectedInfluencer)}
        {this.renderCreatorPerformaceInfo(selectedInfluencer)}
      </div>
    );
  }
}

const mapStateToProps = ({ account, campaignBrief, creatorPerformance, runtimeConfig }): StateProps => ({
  campaign: campaignBrief.brief,
  creatorPerformance: creatorPerformance.data,
  isLoading: creatorPerformance.isLoading,
  isSCP: isInfluencerRealm(account.roles)
    && runtimeConfig.config.zalandoOutfitTaskTypeEnabled
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchCreatorPerformance: (campaignId: number, creatorId: number) => dispatch(fetchCreatorPerformance(campaignId, creatorId)),
  dispatchResetCreatorPerformance: () => dispatch(resetCreatorPerformance())
});

export default connect(mapStateToProps, mapDispatchToProps)(CreatorPerformance);
